import { ActionIcon } from "./view-models/utils";
import { ActionStage } from "./ActionStage";
import {
  ActionViewModel,
  effectorToMapKey,
} from "./view-models/ActionViewModel";
import { Button, Paper, Typography } from "@mui/material";
import { NewActionChooserModal } from "./NewActionChooserModal";
import {
  RigActivityConfigViewModel,
  StructuredActionsViewModel,
} from "../RigActivitiesFormViewModel";
import { TypedPathTo, useFormikValuesAtBasePath } from "portal/utils/typedPath";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import CardsIcon from "common/images/atk-actions/cards_stack.svg?react";
import lodashRange from "lodash/range";
import React, { useCallback, useState } from "react";

function useNewStage(): (
  viewModel: RigActivityConfigViewModel,
  basePath: TypedPathTo<RigActivityConfigViewModel>
) => () => void {
  const { setFieldValue, setFieldTouched } = useFormikContext();

  return useCallback(
    (viewModel, basePath) => () => {
      setFieldValue(basePath.nStages.$path, viewModel.nStages + 1);
      setFieldTouched(basePath.nStages.$path, true);
    },
    [setFieldValue, setFieldTouched]
  );
}

function useMoveStage(): (
  viewModel: RigActivityConfigViewModel,
  basePath: TypedPathTo<RigActivityConfigViewModel>,
  stageIndex: number,
  direction: "up" | "down"
) => () => void {
  const { setFieldValue, setFieldTouched } = useFormikContext();

  return useCallback(
    (viewModel, basePath, stageIndex, direction) => () => {
      // even though the actions are still the same after moving stages,
      // creating a new array and copying the actions into it is sufficient to
      // trigger a re-render, which is necessary for these changes to propagate
      const nextActions: ActionViewModel<unknown>[] = [];

      for (const action of viewModel.actions) {
        if (direction === "up") {
          // moving up; swap stage numbers between this stage and the previous
          // stage
          if (action.startInStageIndex === stageIndex - 1) {
            action.startInStageIndex = stageIndex;
          } else if (action.startInStageIndex === stageIndex) {
            action.startInStageIndex = stageIndex - 1;
          }
        } else {
          // moving down; swap stage numbers between this stage and the next stage
          // eslint-disable-next-line no-lonely-if
          if (action.startInStageIndex === stageIndex + 1) {
            action.startInStageIndex = stageIndex;
          } else if (action.startInStageIndex === stageIndex) {
            action.startInStageIndex = stageIndex + 1;
          }
        }

        nextActions.push(action);
      }

      setFieldValue(basePath.actions.$path, nextActions);
      setFieldTouched(basePath.actions.$path, true);
    },
    [setFieldTouched, setFieldValue]
  );
}

function useDeleteStage(): (
  viewModel: RigActivityConfigViewModel,
  basePath: TypedPathTo<RigActivityConfigViewModel>,
  stageIndex: number,
  onUpdateActions: (
    nextActions: ActionViewModel<unknown>[],
    deletedActionIndexes: number[]
  ) => void
) => () => void {
  const { setFieldValue, setFieldTouched } = useFormikContext();

  return useCallback(
    (viewModel, basePath, stageIndex, onUpdateActions) => () => {
      const nextActions: ActionViewModel<unknown>[] = [];
      const deletedActionIndexes: number[] = [];

      for (
        let actionIndex = 0;
        actionIndex < viewModel.actions.length;
        actionIndex++
      ) {
        const action = viewModel.actions[actionIndex]!;

        if (action.startInStageIndex < stageIndex) {
          nextActions.push(action);
        } else if (action.startInStageIndex === stageIndex) {
          deletedActionIndexes.push(actionIndex);
        } else if (action.startInStageIndex > stageIndex) {
          action.startInStageIndex -= 1;
          nextActions.push(action);
        }
      }

      onUpdateActions(nextActions, deletedActionIndexes);
      setFieldValue(basePath.nStages.$path, viewModel.nStages - 1);
      setFieldTouched(basePath.nStages.$path, true);
    },
    [setFieldTouched, setFieldValue]
  );
}

export const ActionBuilder: React.FC<{
  activityBasePath: TypedPathTo<RigActivityConfigViewModel>;
  structuredModel: StructuredActionsViewModel;
  onEditAction: (actionIndex: number) => void;
  onUpdateActions: (
    nextActions: ActionViewModel<unknown>[],
    deletedActionIndexes: number[]
  ) => void;
}> = ({ activityBasePath, structuredModel, onEditAction, onUpdateActions }) => {
  const { t } = useTranslation();
  const activityViewModel = useFormikValuesAtBasePath(activityBasePath);
  const { setFieldValue, setFieldTouched } =
    useFormikContext<RigActivityConfigViewModel>();
  const newStage = useNewStage();
  const moveStage = useMoveStage();
  const deleteStage = useDeleteStage();
  const [newActionChooserStageIndex, setNewActionChooserStageIndex] = useState<
    number | undefined
  >(undefined);

  const handleNewActionChosen = useCallback(
    (viewModel: ActionViewModel<unknown>) => {
      if (activityViewModel.nStages === 0) {
        setFieldValue(activityBasePath.nStages.$path, 1);
        setFieldTouched(activityBasePath.nStages.$path, true);
      }

      if (viewModel.holdingAction) {
        // remove any actions which start after this holding action.
        const nextActions: ActionViewModel<unknown>[] = [];
        const deletedActionIndexes: number[] = [];

        for (
          let actionIndex = 0;
          actionIndex < activityViewModel.actions.length;
          actionIndex++
        ) {
          const action = activityViewModel.actions[actionIndex]!;

          if (
            effectorToMapKey(action.effector) ===
              effectorToMapKey(viewModel.effector) &&
            action.startInStageIndex >= viewModel.startInStageIndex
          ) {
            // exclude this action
            deletedActionIndexes.push(actionIndex);
            continue;
          }

          nextActions.push(action);
        }

        nextActions.push(viewModel);
        onUpdateActions(nextActions, deletedActionIndexes);
      } else {
        // quick action.  everything's way easier.
        onUpdateActions([...activityViewModel.actions, viewModel], []);
      }

      setNewActionChooserStageIndex(undefined);
    },
    [
      activityBasePath.nStages,
      activityViewModel.actions,
      activityViewModel.nStages,
      onUpdateActions,
      setFieldTouched,
      setFieldValue,
    ]
  );

  return (
    <Paper variant="outlined" className="rounded-xl h-[40vh] overflow-y-scroll">
      <div className="flex flex-col gap-2 p-2">
        <NewActionChooserModal
          activityBasePath={activityBasePath}
          stageIndex={newActionChooserStageIndex ?? 0}
          open={newActionChooserStageIndex !== undefined}
          onDone={handleNewActionChosen}
          onClose={() => setNewActionChooserStageIndex(undefined)}
        />

        {activityViewModel.nStages === 0 ? (
          <div className="mx-auto w-1/2 flex flex-col gap-4 pt-8 text-center items-center">
            <ActionIcon Icon={CardsIcon} isHoldingAction={false} />
            <Typography variant="h6">
              {t(
                "views.autotractor.autonomousActivities.action.builderEmptyHeadline"
              )}
            </Typography>
            <Typography variant="body1">
              {t(
                "views.autotractor.autonomousActivities.action.builderEmptyDetail"
              )}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setNewActionChooserStageIndex(0)}
              size="large"
            >
              {t("utils.actionPhrases.newAction")}
            </Button>
          </div>
        ) : (
          <>
            {lodashRange(activityViewModel.nStages).map((_, i) => (
              <ActionStage
                structuredModel={structuredModel}
                basePath={activityBasePath}
                key={i}
                stageIndex={i}
                onMoveUp={
                  i === 0
                    ? undefined
                    : moveStage(activityViewModel, activityBasePath, i, "up")
                }
                onMoveDown={
                  i === activityViewModel.nStages - 1
                    ? undefined
                    : moveStage(activityViewModel, activityBasePath, i, "down")
                }
                openNewActionChooser={() => setNewActionChooserStageIndex(i)}
                onDelete={deleteStage(
                  activityViewModel,
                  activityBasePath,
                  i,
                  onUpdateActions
                )}
                onEditAction={onEditAction}
              />
            ))}
            <Button
              variant="outlined"
              color="secondary"
              onClick={newStage(activityViewModel, activityBasePath)}
            >
              {/* provide some extra contrast against the background relative to secondary text */}
              <Typography variant="inherit" color="textPrimary">
                {t("utils.actionPhrases.newStage")}
              </Typography>
            </Button>
          </>
        )}
      </div>
    </Paper>
  );
};
